import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const ProgressRing = ({ percentage, size = 80, strokeWidth = 5 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={radius}
        fill="none" stroke="var(--symbolica-warm-border)" strokeWidth={strokeWidth} />
      <circle cx={size/2} cy={size/2} r={radius}
        fill="none" stroke="var(--symbolica-accent)" strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
    </svg>
  );
};

/* ─── Info Modal (portalled, covers everything) ─── */
const InfoModal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(34,34,34,0.35)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--symbolica-white)',
        borderRadius: '12px',
        width: '94%',
        maxWidth: '640px',
        maxHeight: '88vh',
        overflow: 'auto',
        boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '18px 24px',
          borderBottom: '1px solid var(--symbolica-warm-border)',
          position: 'sticky', top: 0, background: 'var(--symbolica-white)', zIndex: 1,
          borderRadius: '12px 12px 0 0',
        }}>
          <h3 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 400 }}>{title}</h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: 20, color: 'var(--symbolica-muted)',
            cursor: 'pointer', padding: '4px 8px', lineHeight: 1, borderRadius: 4,
          }}>&times;</button>
        </div>
        <div style={{ padding: '24px', fontSize: 14, lineHeight: 1.7, color: 'var(--symbolica-black)' }}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

const SectionLabel = ({ children }) => (
  <div style={{
    fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 400,
    color: 'var(--symbolica-muted)',
    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10,
  }}>{children}</div>
);

const officeSectionLabelStyle = {
  fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
  color: 'var(--symbolica-muted)', marginBottom: 10, marginTop: 20,
};

// Render admin-edited handbook copy. Strings may contain `**bold**` segments;
// we split on those rather than using dangerouslySetInnerHTML so that even if
// an admin pastes raw HTML into a PUT body it just renders as literal text.
const renderInlineBold = (text) => {
  if (text == null) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4
      ? <strong key={i}>{part.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
};

/* ─── Office modal body, driven by /api/handbook ─── */
// Renders an office's first-day intro, address, map, transit notes, lockbox
// info, and Notion link from a content dict the backend hands us. Anything
// not provided by the API is simply skipped, so an admin who blanks out
// (say) the lockbox code via PUT just removes that section -- nothing
// crashes, no stale string lingers.
const OfficeModalBody = ({ office, loading }) => {
  if (loading) {
    return <p style={{ marginTop: 0, color: 'var(--symbolica-muted)' }}>Loading office details&hellip;</p>;
  }
  if (!office || Object.keys(office).length === 0) {
    return <p style={{ marginTop: 0, color: 'var(--symbolica-muted)' }}>Office details aren't available right now. Ask in #ops if you need them.</p>;
  }
  return (
    <>
      {office.first_day && (
        <>
          <h4 style={{ ...officeSectionLabelStyle, marginTop: 0 }}>First Day?</h4>
          <p style={{ marginTop: 0 }}>{office.first_day}</p>
        </>
      )}
      {(office.access_contact_name || office.access_contact_email || office.access_contact_phone) && (
        <p style={{ fontSize: 13, color: 'var(--symbolica-muted)' }}>
          Issues with access?{office.access_contact_name ? ` Contact ${office.access_contact_name}` : ''}
          {office.access_contact_email && (
            <> &mdash; <a href={`mailto:${office.access_contact_email}`} style={{ color: 'var(--symbolica-periwinkle)' }}>{office.access_contact_email}</a></>
          )}
          {office.access_contact_phone && <>, {office.access_contact_phone}</>}.
        </p>
      )}
      {office.address && (
        <>
          <h4 style={officeSectionLabelStyle}>Address</h4>
          <div style={{
            background: '#F0EDE6', borderRadius: 'var(--radius-sm)', padding: '14px 18px',
            fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6, marginBottom: 16, whiteSpace: 'pre-line',
          }}>
            {office.address}
          </div>
        </>
      )}
      {office.map_embed_url && (
        <div style={{ borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 16, border: '1px solid var(--symbolica-warm-border)' }}>
          <iframe
            title={`${office.title || 'Office'} Map`}
            src={office.map_embed_url}
            width="100%" height="220" style={{ border: 0 }} allowFullScreen loading="lazy"
          />
        </div>
      )}
      {office.getting_there && (
        <>
          <h4 style={officeSectionLabelStyle}>Getting There</h4>
          <p>{office.getting_there}</p>
        </>
      )}
      {office.daily_access && (
        <>
          <h4 style={officeSectionLabelStyle}>Daily Access</h4>
          <p>{office.daily_access}</p>
        </>
      )}
      {office.lockbox_code && (
        <div style={{
          background: '#F0EDE6', borderRadius: 'var(--radius-sm)', padding: '14px 18px',
          fontSize: 14, marginBottom: 16,
        }}>
          <strong>Lockbox Code:</strong>{' '}
          <code style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700, letterSpacing: '0.1em' }}>
            {office.lockbox_code}
          </code>
          {office.lockbox_instructions && (
            <div style={{ fontSize: 12, color: 'var(--symbolica-muted)', marginTop: 4 }}>
              {office.lockbox_instructions}
            </div>
          )}
        </div>
      )}
      {office.lost_access && (
        <>
          <h4 style={officeSectionLabelStyle}>Lost Your Access Card?</h4>
          <p>{office.lost_access}</p>
        </>
      )}
      {office.notion_url && (
        <div style={{ marginTop: 20 }}>
          <a href={office.notion_url}
            target="_blank" rel="noopener noreferrer" className="primary-button"
            style={{ textDecoration: 'none', display: 'inline-block' }}>
            More information on Notion
          </a>
        </div>
      )}
    </>
  );
};

/* ─── Key Link Card ─── */
const KeyLinkCard = ({ title, subtitle, onClick }) => (
  <button onClick={onClick} style={{
    background: 'var(--symbolica-card)',
    border: '1px solid var(--symbolica-warm-border)',
    borderRadius: 'var(--radius)',
    padding: '20px',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'all 0.12s ease',
    fontFamily: 'var(--font-body)',
    display: 'flex', flexDirection: 'column', gap: 6,
    width: '100%',
  }}
    onMouseEnter={e => e.currentTarget.style.background = '#F0EDE6'}
    onMouseLeave={e => e.currentTarget.style.background = 'var(--symbolica-card)'}
  >
    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--symbolica-black)' }}>{title}</div>
    <div style={{ fontSize: 12, color: 'var(--symbolica-muted)', lineHeight: 1.4 }}>{subtitle}</div>
  </button>
);

export default function OnboardingPage({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPreMigration, setIsPreMigration] = useState(false);
  const [submitting, setSubmitting] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  // Office details (address, lockbox code, contact phone, etc.) come from
  // GET /api/handbook so they no longer ship in the static React bundle.
  // Keyed by handbook entry id; null = still loading; {} = empty/missing.
  const [handbookEntries, setHandbookEntries] = useState(null);

  useEffect(() => {
    if (user) {
      fetchOnboardingItems();
      fetchHandbook();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchOnboardingItems = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/onboarding/items`, {
        headers: { 'X-User-Email': user?.email || '' },
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to fetch onboarding items');
      const data = await res.json();
      setItems(data.items || []);
      setIsPreMigration(data.is_pre_migration || false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchHandbook = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/handbook`, { credentials: 'include' });
      if (!res.ok) throw new Error(`handbook fetch failed (${res.status})`);
      const data = await res.json();
      // Flatten to { entry_id: contentDict } so the JSX below stays tidy.
      const flat = {};
      Object.entries(data.entries || {}).forEach(([id, row]) => {
        flat[id] = row?.content || {};
      });
      setHandbookEntries(flat);
    } catch (err) {
      console.error('Error fetching handbook:', err);
      setHandbookEntries({});
    }
  };

  const londonOffice = handbookEntries?.office_london || null;
  const sfOffice = handbookEntries?.office_san_francisco || null;
  const aboutUs = handbookEntries?.about_us || null;
  const welcomeBlurb = handbookEntries === null ? null : (handbookEntries.welcome_blurb || {});

  const toggleItemCompletion = async (itemId, currentStatus) => {
    setSubmitting(itemId);
    try {
      const endpoint = currentStatus ? 'incomplete' : 'complete';
      const res = await fetch(`${API_BASE}/api/onboarding/items/${itemId}/${endpoint}`, {
        method: 'POST',
        headers: { 'X-User-Email': user?.email || '' },
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to update item');
      setItems(items.map(item =>
        item.id === itemId
          ? { ...item, progress: { status: currentStatus ? 'incomplete' : 'completed', completed: !currentStatus, updated_at: new Date().toISOString() } }
          : item
      ));
    } catch (err) {
      console.error('Error updating item:', err);
    } finally {
      setSubmitting(null);
    }
  };

  const getItemStatus = (item) => {
    if (item.progress?.status) return item.progress.status;
    if (item.progress?.completed !== undefined) return item.progress.completed ? 'completed' : 'incomplete';
    return 'incomplete';
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--symbolica-muted)' }}>Loading...</div>;
  if (error) return <div style={{ padding: 40, textAlign: 'center', color: '#B91C1C' }}>Error: {error}</div>;
  if (!user) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--symbolica-muted)' }}>Please log in.</div>;

  const completedCount = items.filter(i => { const s = getItemStatus(i); return s === 'completed' || s === 'na'; }).length;
  const totalCount = items.length;
  const pct = totalCount > 0 ? Math.round(completedCount / totalCount * 100) : 0;
  const incomplete = items.filter(i => getItemStatus(i) === 'incomplete');
  const completed = items.filter(i => getItemStatus(i) === 'completed');
  const na = items.filter(i => getItemStatus(i) === 'na');

  return (
    <div className="page-wrap">

      {/* ════════ Onboarding Status Banner ════════ */}
      {totalCount > 0 && incomplete.length > 0 && (
        <div style={{
          background: 'var(--symbolica-periwinkle)',
          color: '#fff',
          borderRadius: 'var(--radius)',
          padding: '14px 20px',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
          fontSize: 11,
          fontFamily: 'var(--font-mono)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}>
          <span>{Math.round((incomplete.length / totalCount) * 100)}% of onboarding tasks still remaining &mdash; please complete</span>
          <span style={{ fontWeight: 600 }}>{incomplete.length} of {totalCount} to do</span>
        </div>
      )}

      {/* ════════ Welcome Header ════════ */}
      <h1 className="content-title">Welcome to Symbolica</h1>
      <div style={{
        background: 'var(--symbolica-card)',
        border: '1px solid var(--symbolica-warm-border)',
        borderRadius: 'var(--radius)',
        padding: '22px 24px',
        marginBottom: 32,
        fontSize: 14, lineHeight: 1.65, color: 'var(--symbolica-black)',
      }}>
        {welcomeBlurb === null ? (
          <div style={{ color: 'var(--symbolica-muted)', fontSize: 13 }}>Loading&hellip;</div>
        ) : (
          <>
            {welcomeBlurb.intro && (
              <p style={{ margin: '0 0 10px' }}>{renderInlineBold(welcomeBlurb.intro)}</p>
            )}
            {Array.isArray(welcomeBlurb.tracks) && welcomeBlurb.tracks.length > 0 && (
              <ul style={{ margin: '0 0 14px', paddingLeft: 20 }}>
                {welcomeBlurb.tracks.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            )}
            {(welcomeBlurb.tip_prefix || welcomeBlurb.tip_shortcut || welcomeBlurb.tip_suffix) && (
              <p style={{ margin: 0, fontSize: 12, color: 'var(--symbolica-muted)' }}>
                {welcomeBlurb.tip_prefix} {welcomeBlurb.tip_shortcut && (
                  <strong>{welcomeBlurb.tip_shortcut}</strong>
                )} {welcomeBlurb.tip_suffix}
              </p>
            )}
          </>
        )}
      </div>

      {/* ════════ Key Links ════════ */}
      <SectionLabel>Key Links</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12, marginBottom: 32 }}>
        <KeyLinkCard
          title="About Us"
          subtitle="Learn about Symbolica's mission, vision, and what we're building."
          onClick={() => setActiveModal('about')}
        />
        <KeyLinkCard
          title="London Office"
          subtitle="Address, directions, access cards, and first-day info."
          onClick={() => setActiveModal('london')}
        />
        <KeyLinkCard
          title="San Francisco Office"
          subtitle="Address, directions, lockbox code, and first-day info."
          onClick={() => setActiveModal('sf')}
        />
      </div>

      {/* ════════ Modals ════════ */}

      {/* About Us */}
      <InfoModal
        isOpen={activeModal === 'about'}
        onClose={() => setActiveModal(null)}
        title={aboutUs?.title || 'About Us'}
      >
        {handbookEntries === null ? (
          <div style={{ color: 'var(--symbolica-muted)' }}>Loading&hellip;</div>
        ) : (
          <>
            {Array.isArray(aboutUs?.paragraphs) && aboutUs.paragraphs.map((p, i) => (
              <p key={i} style={i === 0 ? { marginTop: 0 } : undefined}>
                {renderInlineBold(p)}
              </p>
            ))}
            {aboutUs?.notion_url && (
              <div style={{ marginTop: 16 }}>
                <a
                  href={aboutUs.notion_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="primary-button"
                  style={{ textDecoration: 'none', display: 'inline-block' }}
                >
                  {aboutUs.notion_link_text || 'Find out more on Notion'}
                </a>
              </div>
            )}
          </>
        )}
      </InfoModal>

      {/* London Office */}
      <InfoModal
        isOpen={activeModal === 'london'}
        onClose={() => setActiveModal(null)}
        title={londonOffice?.title || 'London Office'}
      >
        <OfficeModalBody office={londonOffice} loading={handbookEntries === null} />
      </InfoModal>

      {/* San Francisco Office */}
      <InfoModal
        isOpen={activeModal === 'sf'}
        onClose={() => setActiveModal(null)}
        title={sfOffice?.title || 'San Francisco Office'}
      >
        <OfficeModalBody office={sfOffice} loading={handbookEntries === null} />
      </InfoModal>

      {/* ════════ Onboarding Tasks ════════ */}
      <SectionLabel>Onboarding Tasks</SectionLabel>

      {/* Progress header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 24,
        flexWrap: 'wrap',
        background: 'var(--symbolica-card)',
        border: '1px solid var(--symbolica-warm-border)',
        borderRadius: 'var(--radius)',
        padding: '20px 24px',
        marginBottom: 24,
      }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <ProgressRing percentage={pct} size={64} strokeWidth={4} />
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, fontWeight: 600, color: 'var(--symbolica-black)',
          }}>
            {pct}%
          </div>
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--symbolica-black)', marginBottom: 2 }}>
            {completedCount === totalCount ? 'All done!' : `${completedCount} of ${totalCount} completed`}
          </div>
          <div style={{ fontSize: 13, color: 'var(--symbolica-muted)', lineHeight: 1.5 }}>
            {isPreMigration
              ? 'As an existing team member, all items have been marked complete.'
              : completedCount === totalCount
                ? 'You\'ve finished your onboarding. Welcome to the team!'
                : 'Complete the tasks below to get started.'}
          </div>
        </div>
      </div>

      {/* Priority cards */}
      {incomplete.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 12,
          }}>
            {incomplete.slice(0, 3).map(item => (
              <div key={item.id} style={{
                background: 'var(--symbolica-card)',
                border: '1px solid var(--symbolica-warm-border)',
                borderRadius: 'var(--radius)',
                padding: '18px',
                display: 'flex', flexDirection: 'column', gap: 8,
              }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--symbolica-periwinkle)' }}>
                  Priority
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--symbolica-black)', lineHeight: 1.3 }}>
                  {item.title}
                </div>
                <div style={{ fontSize: 12, color: 'var(--symbolica-muted)', lineHeight: 1.4, flex: 1 }}>
                  {item.description}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  {item.link && (
                    <a href={item.link} target="_blank" rel="noopener noreferrer"
                      className="primary-button"
                      style={{ fontSize: 12, padding: '6px 14px', textDecoration: 'none' }}>
                      Open Link
                    </a>
                  )}
                  <button onClick={() => toggleItemCompletion(item.id, false)}
                    disabled={submitting === item.id || isPreMigration}
                    className="secondary-button" style={{ fontSize: 12, padding: '6px 14px' }}>
                    Mark Done
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Remaining to do */}
      {incomplete.length > 3 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{
            background: 'var(--symbolica-card)',
            border: '1px solid var(--symbolica-warm-border)',
            borderRadius: 'var(--radius)', overflow: 'hidden',
          }}>
            {incomplete.slice(3).map((item, i) => (
              <div key={item.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: 14,
                padding: '14px 20px',
                borderBottom: i < incomplete.length - 4 ? '1px solid var(--symbolica-warm-border)' : 'none',
              }}>
                <button onClick={() => toggleItemCompletion(item.id, false)}
                  disabled={submitting === item.id || isPreMigration}
                  style={{
                    width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                    border: '1.5px solid var(--symbolica-warm-border)',
                    background: 'none', cursor: 'pointer', marginTop: 1,
                  }}
                  aria-label="Mark as complete" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--symbolica-black)', marginBottom: 2 }}>{item.title}</div>
                  <div style={{ fontSize: 13, color: 'var(--symbolica-muted)', lineHeight: 1.4 }}>{item.description}</div>
                </div>
                {item.link && (
                  <a href={item.link} target="_blank" rel="noopener noreferrer"
                    className="secondary-button"
                    style={{ fontSize: 12, padding: '6px 14px', flexShrink: 0, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                    Open Link
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completed */}
      {completed.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <SectionLabel>Completed ({completed.length})</SectionLabel>
          <div style={{
            background: 'var(--symbolica-card)',
            border: '1px solid var(--symbolica-warm-border)',
            borderRadius: 'var(--radius)', overflow: 'hidden',
          }}>
            {completed.map((item, i) => (
              <div key={item.id} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '12px 20px',
                borderBottom: i < completed.length - 1 ? '1px solid var(--symbolica-warm-border)' : 'none',
                opacity: 0.55,
              }}>
                <button onClick={() => toggleItemCompletion(item.id, true)}
                  disabled={submitting === item.id || isPreMigration}
                  style={{
                    width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                    border: '1.5px solid var(--symbolica-accent)', background: 'var(--symbolica-accent)',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                  }}
                  aria-label="Mark as incomplete">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--symbolica-black)" strokeWidth="3">
                    <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <div style={{ flex: 1, fontSize: 14, color: 'var(--symbolica-black)' }}>{item.title}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* N/A */}
      {na.length > 0 && (
        <div>
          <SectionLabel>Not Applicable ({na.length})</SectionLabel>
          <div style={{
            background: 'var(--symbolica-card)',
            border: '1px solid var(--symbolica-warm-border)',
            borderRadius: 'var(--radius)', overflow: 'hidden', opacity: 0.5,
          }}>
            {na.map((item, i) => (
              <div key={item.id} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '12px 20px',
                borderBottom: i < na.length - 1 ? '1px solid var(--symbolica-warm-border)' : 'none',
              }}>
                <div style={{
                  width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                  background: '#F0EDE6', border: '1.5px solid var(--symbolica-warm-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, color: 'var(--symbolica-muted)',
                }}>&minus;</div>
                <div style={{ flex: 1, fontSize: 13, color: 'var(--symbolica-muted)' }}>{item.title}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
